import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { C, card, label, btn, SIGNS, GLYPH } from "../lib/theme";
import ChartWheel from "../lib/ChartWheel";

export default function PublicProfile() {
  const { name } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const load = () => api.publicProfile(name).then(setP).catch((e) => setErr(e.message));
  useEffect(() => { load(); }, [name]);   // load returns a Promise — never hand it to React as a cleanup
  if (err) return <div style={{ color: C.rose }}>{err}</div>;
  if (!p) return <div style={{ color: C.faint }}>Loading…</div>;

  const lotsWithLon = p.chart?.lots
    ? Object.fromEntries(Object.entries(p.chart.lots).map(([k, v]) =>
        [k, { lon: SIGNS.indexOf(v.sign) * 30 + v.degree }]))
    : null;
  const posWithLon = p.chart?.placements && p.chart_privacy === "full"
    ? Object.fromEntries(Object.entries(p.chart.placements).map(([k, v]) => [k, v.lon]))
    : null;

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ ...card, marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 14, alignItems: "baseline", flexWrap: "wrap" }}>
          <span style={{ fontFamily: C.displayFont, fontSize: 26 }}>
            {p.display_name}</span>
          <span style={{ fontSize: 10, letterSpacing: "0.1em",
            color: p.title_kind === "custom" ? C.brass : C.faint,
            border: `1px solid ${p.title_kind === "custom" ? C.brass : C.line}`,
            borderRadius: 3, padding: "2px 6px" }}>
            {p.title_kind === "custom" && "✶ "}{p.title.toUpperCase()}</span>
          {p.title_kind === "custom" && <span style={{ fontSize: 10, color: C.faint }}>
            earned: {p.earned_title}</span>}
          {p.plan === "professional" && <span style={{ color: C.brass, fontSize: 12 }}>✶ professional</span>}
          <button onClick={() => (p.i_follow ? api.unfollow(name) : api.follow(name)).then(load)}
            style={{ ...btn(p.i_follow ? false : true), marginLeft: "auto",
              padding: "5px 16px", fontSize: 12 }}>
            {p.i_follow ? "following ✓" : "follow"}</button>
        </div>
        <div style={{ fontSize: 12, color: C.dim, marginTop: 6 }}>
          ◆ {p.worth_reading_received} worth reading · {p.followers} follower{p.followers === 1 ? "" : "s"}
          {" "}· member since {String(p.member_since).slice(0, 10)}
          {p.age !== null && <> · age {p.age}</>}
          {p.location && <> · {p.location}</>}
          {p.self_reported_type && <> · {p.self_reported_type} (self-reported)</>}
        </div>
        {p.signature && <a href={p.signature} target="_blank" rel="noreferrer"
          style={{ color: C.brass, fontSize: 12, textDecoration: "none" }}>
          {p.signature.replace("https://", "")}</a>}
      </div>

      <div style={card}>
        <div style={label}>CHART · {(p.chart_privacy || "none").toUpperCase()}</div>
        {!p.chart && <div style={{ fontSize: 12.5, color: C.faint }}>
          This member keeps their chart private.</div>}
        {p.chart && p.chart_privacy === "big3" && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {[["Rising", p.chart.rising], ["Sun", p.chart.sun], ["Moon", p.chart.moon]]
              .map(([k, s]) => (
              <div key={k} style={{ background: C.surface2, border: `1px solid ${C.line}`,
                borderRadius: 6, padding: "10px 18px", textAlign: "center" }}>
                <div style={{ fontSize: 22 }}>{GLYPH[s]}</div>
                <div style={{ fontSize: 12.5 }}>{s}</div>
                <div style={{ fontSize: 10, color: C.faint, letterSpacing: "0.1em" }}>{k.toUpperCase()}</div>
              </div>))}
          </div>)}
        {p.chart && p.chart_privacy === "placements" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 6 }}>
            <div style={{ fontSize: 12.5 }}>Rising: {GLYPH[p.chart.rising]} {p.chart.rising}</div>
            {Object.entries(p.chart.placements).map(([b, s]) => (
              <div key={b} style={{ fontSize: 12.5 }}>{b}: {GLYPH[s]} {s}</div>))}
          </div>)}
        {p.chart && p.chart_privacy === "full" && (
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-start" }}>
            <ChartWheel positions={posWithLon} lots={lotsWithLon} asc={p.chart.rising} size={330} />
            <table style={{ borderCollapse: "collapse", fontSize: 12 }}>
              <tbody>
                {Object.entries(p.chart.placements).map(([b, v]) => (
                  <tr key={b} style={{ borderBottom: `1px solid ${C.line}` }}>
                    <td style={{ padding: "3px 8px 3px 0" }}>{b}</td>
                    <td style={{ padding: "3px 8px" }}>{GLYPH[v.sign]} {v.sign}</td>
                    <td style={{ padding: "3px 8px", fontFamily: "ui-monospace,monospace" }}>
                      {v.degree.toFixed(1)}°</td>
                    <td style={{ padding: "3px 0", color: C.faint }}>house {v.house}</td>
                  </tr>))}
              </tbody>
            </table>
          </div>)}
      </div>
    </div>
  );
}
